import { useCallback, useEffect, useState } from "react";
import { Briefcase, Loader2, Mail, Trash2, UserCheck, KeyRound, Clock } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, beDate, inputCls, btnPrimary } from "@/lib/studioApi";
import { SectionCard } from "@/components/studio/StudioUI";
import { AccountantCard } from "@/components/studio/AccountantCard";
import { AccountantQuestions } from "@/components/studio/AccountantQuestions";

/* Accès à l'application pour le comptable : invitation (rôle comptable), accès actifs, invitations en attente. */
const AccountantAccess = () => {
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => studio.get("/team").then(({ data }) => setTeam(data)).catch(() => setTeam({ members: [], invitations: [] })), []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.email.trim()) return;
    setBusy(true);
    try {
      await studio.post("/team/invite", { ...form, role: "comptable" });
      toast.success("Invitation envoyée : votre comptable choisit son mot de passe et accède à son espace.");
      setForm({ email: "", name: "" });
      load();
    } catch (e) { toast.error(apiError(e, "Invitation impossible.")); } finally { setBusy(false); }
  };

  const remove = async (email) => {
    try {
      await studio.delete(`/team/${encodeURIComponent(email)}`);
      toast.success("Accès retiré.");
      load();
    } catch (e) { toast.error(apiError(e, "Suppression impossible.")); }
  };

  const members = (team?.members || []).filter((m) => m.member_role === "comptable");
  const pending = (team?.invitations || []).filter((i) => (i.member_role || "ouvrier") === "comptable");

  return (
    <SectionCard
      testId="accountant-access-card"
      className="mt-6"
      title={<span className="inline-flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#3A5CC8]" /> Accès à l'application pour votre comptable</span>}
      hint="Lecture seule : factures de vente, achats, export comptable"
    >
      <p className="mb-4 text-sm leading-relaxed text-[#5C6A87]">Votre comptable se connecte sur Devisto avec son propre mot de passe et ne voit que vos factures de vente (PDF + UBL), vos achats et l'export comptable. Ni devis, ni chantiers, aucune modification possible.</p>
      {team === null ? <div className="h-16 animate-pulse rounded-xl bg-[#F6F8FD]" /> : (
        <>
          {members.map((m) => (
            <div key={m.email} data-testid={`accountant-member-${m.email}`} className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white text-emerald-600"><UserCheck className="h-4 w-4" /></span>
                <div><p className="text-sm font-semibold text-[#0F2645]">{m.name || m.email}</p><p className="text-xs text-[#5C6A87]">{m.email} · accès actif depuis le {beDate(m.created_at)}</p></div>
              </div>
              <button type="button" data-testid={`accountant-member-remove-${m.email}`} onClick={() => remove(m.email)} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl border border-red-200 px-3 text-xs font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Retirer l'accès</button>
            </div>
          ))}
          {pending.map((i) => (
            <div key={i.email} data-testid={`accountant-invite-pending-${i.email}`} className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white text-amber-600"><Clock className="h-4 w-4" /></span>
                <div><p className="text-sm font-semibold text-[#0F2645]">{i.name || i.email}</p><p className="text-xs text-amber-800">{i.email} · invitation envoyée le {beDate(i.created_at)}, en attente</p></div>
              </div>
              <button type="button" data-testid={`accountant-invite-cancel-${i.email}`} onClick={() => remove(i.email)} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 text-xs font-semibold text-amber-800 hover:bg-amber-100"><Trash2 className="h-3.5 w-3.5" /> Annuler</button>
            </div>
          ))}
          {!members.length && !pending.length && (
            <p data-testid="accountant-access-empty" className="mb-3 text-sm text-[#5C6A87]">Aucun accès comptable pour l'instant.</p>
          )}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1.4fr_1fr_auto]">
            <input data-testid="accountant-invite-email" className={inputCls} placeholder="email@fiduciaire.be" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="accountant-invite-name" className={inputCls} placeholder="Nom du comptable / fiduciaire" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <button type="button" data-testid="accountant-invite-btn" disabled={busy || !form.email.trim()} onClick={invite} className={btnPrimary}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Donner l'accès
            </button>
          </div>
        </>
      )}
    </SectionCard>
  );
};

export default function StudioAccountantHub() {
  return (
    <StudioLayout
      ownerOnly
      title="Mon comptable"
      subtitle="Tout ce qui concerne votre comptable au même endroit : envoi automatique chaque trimestre, accès à l'application en lecture seule, questions sur vos factures."
    >
      <div data-testid="accountant-hub" className="mt-6">
        <AccountantCard />
      </div>
      <AccountantAccess />
      <AccountantQuestions canReply className="mt-6" />
      <p className="mt-6 inline-flex items-start gap-2 text-xs leading-relaxed text-[#5C6A87]">
        <Briefcase className="h-3.5 w-3.5 shrink-0 text-[#3A5CC8]" />
        Le comptable ne voit jamais vos devis, vos chantiers ni vos marges : uniquement ventes, achats, règlements et exports.
      </p>
    </StudioLayout>
  );
}
